import { useEffect, useMemo, useState } from "react";
import { Copy, ExternalLink, Trash2 } from "lucide-react";
import { useDirtyTracking } from "../../settings/defaults/useDirtyTracking";
import { useActiveTenant } from "../../../auth/AuthProvider";
import { EditorTopBar } from "./EditorTopBar";
import { useLandingPagesForDropdown } from "./useLandingPagesForDropdown";
import { usePagesForDropdown } from "./usePagesForDropdown";
import { formatLongDate } from "../date-utils";

export type CustomUrlTargetType = "landing_page" | "internal_path";

export interface CustomUrlFormValues {
  slug: string;
  targetType: CustomUrlTargetType;
  targetLandingPageId: string | null;
  targetPath: string;
  isActive: boolean;
}

interface Props {
  mode: "create" | "edit";
  initialValues: CustomUrlFormValues;
  /** Last saved updatedAt — surfaced in the status panel. */
  updatedAt: string | null;
  isSaving: boolean;
  saveError?: string | null;
  onSave: (values: CustomUrlFormValues) => Promise<void>;
  onDelete?: () => void;
  isDeleting?: boolean;
}

const SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

/**
 * Two-column custom URL editor. Slug + target type + target picker
 * stack in the main column; status and copy URL button in the right rail.
 * Pure presentation w.r.t. data — the Create/Edit wrappers own GraphQL.
 */
export function CustomUrlEditor({
  mode,
  initialValues,
  updatedAt,
  isSaving,
  saveError,
  onSave,
  onDelete,
  isDeleting,
}: Props) {
  const tenantDomain = useActiveTenant().domain;

  const tracking = useDirtyTracking<CustomUrlFormValues>(initialValues);

  // Re-sync the dirty tracker when the saved record changes (e.g. after a
  // successful save refreshes the Apollo cache) — same hydration pattern
  // as LandingPageEditor.
  const [hydratedKey, setHydratedKey] = useState<string>(
    JSON.stringify(initialValues),
  );
  useEffect(() => {
    const next = JSON.stringify(initialValues);
    if (next !== hydratedKey) {
      tracking.markSaved(initialValues);
      setHydratedKey(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  // Use Apollo Client for landing pages query
  const { landingPages, loading: landingPagesLoading } = useLandingPagesForDropdown();
  // Published CMS pages for the page picker convenience
  const { pages: cmsPages, loading: cmsPagesLoading } = usePagesForDropdown();

  const v = tracking.values;
  const setField = tracking.setField;

  // Auto-slug from target path for new URLs until the user touches the
  // slug field directly.
  const [autoSlug, setAutoSlug] = useState(mode === "create");
  useEffect(() => {
    if (!autoSlug) return;
    const next = slugifyPath(tracking.values.targetPath);
    if (next !== tracking.values.slug && next) {
      setField("slug", next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tracking.values.targetPath, autoSlug]);

  // Client-side slug validation — mirrors the server rule (no leading or
  // trailing dash) so rejections surface before save.
  const slugError = useMemo(() => {
    const trimmed = v.slug.trim();
    if (!trimmed) return tracking.isDirty ? "Slug is required" : null;
    if (!SLUG_PATTERN.test(trimmed))
      return "Lowercase letters, numbers, and dashes only — can't start or end with a dash.";
    return null;
  }, [v.slug, tracking.isDirty]);

  const canSave = useMemo(() => {
    if (isSaving || isDeleting) return false;
    if (!v.slug.trim()) return false;
    if (slugError) return false;
    if (mode === "edit" && !tracking.isDirty) return false;
    return true;
  }, [isSaving, isDeleting, v.slug, slugError, mode, tracking.isDirty]);

  const saveLabel = mode === "edit" ? "Save changes" : "Create URL";

  async function handleSave() {
    await onSave(v);
  }

  const copyUrl = async () => {
    const fullUrl = `https://${tenantDomain}/${v.slug}`;
    try {
      await navigator.clipboard.writeText(fullUrl);
    } catch {
      // Silent fail
    }
  };

  const selectedLandingPage = useMemo(
    () => landingPages.find((p) => p.id === v.targetLandingPageId),
    [landingPages, v.targetLandingPageId],
  );

  // Build landing page options for select
  const landingPageOptions = useMemo(() => {
    return landingPages.map((page) => ({
      value: page.id,
      label: `${page.headline} (${page.slug})`,
    }));
  }, [landingPages]);

  return (
    <div className="min-h-full bg-paper">
      <EditorTopBar
        title={v.slug || "Untitled URL"}
        isActive={v.isActive}
        isDirty={tracking.isDirty}
        isSaving={isSaving}
        saveLabel={saveLabel}
        canSave={canSave}
        onDiscard={mode === "edit" ? tracking.reset : undefined}
        onSave={handleSave}
      />

      <div className="grid min-h-[calc(100vh-3.5rem-var(--banner-h))] grid-cols-[1fr_360px] gap-0">
        {/* Main column */}
        <div className="flex min-w-0 flex-col">
          <div className="px-8 pb-6 pt-8">
            {saveError && (
              <div className="mb-3 rounded-md bg-rust-s/40 px-3 py-2 text-[12.5px] text-rust">
                {saveError}
              </div>
            )}

            <div>
              <label className="mb-1 block text-[11px] font-medium uppercase tracking-wider text-ink-4">
                Slug
              </label>
              <div className="hair flex items-center overflow-hidden rounded-md border bg-paper focus-within:border-tide">
                <span className="border-r border-ink/10 bg-canvas/50 px-2.5 py-1.5 font-mono text-[12.5px] text-ink-4">
                  /
                </span>
                <input
                  type="text"
                  value={v.slug}
                  onChange={(e) => {
                    if (autoSlug) setAutoSlug(false);
                    // Auto-lowercase and strip invalid characters
                    const cleaned = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "");
                    setField("slug", cleaned);
                  }}
                  placeholder="my-custom-url"
                  className="flex-1 bg-transparent px-2.5 py-1.5 font-mono text-[12.5px] text-ink-2 focus:outline-none"
                />
              </div>
              {slugError && (
                <div className="mt-1 text-[11px] text-rust">{slugError}</div>
              )}
              {/* Live preview */}
              <div className="mt-1 text-[11.5px] text-ink-4">
                <span className="font-medium">Preview:</span>{" "}
                <span className="font-mono text-tide">
                  https://{tenantDomain}/{v.slug || "..."}
                </span>
              </div>
            </div>

            <div className="mt-6">
              <label className="mb-1 block text-[11px] font-medium uppercase tracking-wider text-ink-4">
                Target type
              </label>
              <div className="hair flex items-center gap-3 rounded-md border bg-paper p-1.5">
                <label className="flex flex-1 cursor-pointer items-center rounded-md border border-ink/20 bg-paper px-3 py-2 hover:border-tide/50 focus-within:border-tide">
                  <input
                    type="radio"
                    name="targetType"
                    value="landing_page"
                    checked={v.targetType === "landing_page"}
                    onChange={() => setField("targetType", "landing_page")}
                    className="accent-tide"
                  />
                  <span className="ml-2 text-[12.5px] text-ink-2">
                    Landing page
                  </span>
                </label>
                <label className="flex flex-1 cursor-pointer items-center rounded-md border border-ink/20 bg-paper px-3 py-2 hover:border-tide/50 focus-within:border-tide">
                  <input
                    type="radio"
                    name="targetType"
                    value="internal_path"
                    checked={v.targetType === "internal_path"}
                    onChange={() => setField("targetType", "internal_path")}
                    className="accent-tide"
                  />
                  <span className="ml-2 text-[12.5px] text-ink-2">
                    Internal path
                  </span>
                </label>
              </div>
            </div>

            {v.targetType === "landing_page" ? (
              <div className="mt-6">
                <label className="mb-1 block text-[11px] font-medium uppercase tracking-wider text-ink-4">
                  Landing page
                </label>
                <div className="hair flex items-center overflow-hidden rounded-md border bg-paper focus-within:border-tide">
                  <span className="border-r border-ink/10 bg-canvas/50 px-2.5 py-1.5 font-mono text-[12.5px] text-ink-4">
                    /landing/
                  </span>
                  <select
                    value={v.targetLandingPageId || ""}
                    onChange={(e) => {
                      setField("targetLandingPageId", e.target.value || null);
                      if (autoSlug && e.target.value) {
                        // Find the selected page's slug for auto-slug
                        const page = landingPages.find((p) => p.id === e.target.value);
                        if (page && page.slug) {
                          const nextSlug = slugifyPath(`/landing/${page.slug}`);
                          if (nextSlug) setField("slug", nextSlug);
                        }
                      }
                    }}
                    disabled={landingPagesLoading}
                    className="flex-1 bg-transparent px-2.5 py-1.5 font-mono text-[12.5px] text-ink-2 focus:outline-none disabled:opacity-50"
                  >
                    <option value="">Select a landing page...</option>
                    {landingPageOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                {selectedLandingPage && (
                  <div className="mt-1 text-[11.5px] text-ink-4">
                    <span className="font-medium">Selected:</span>{" "}
                    <span className="font-mono text-ink-3">
                      /landing/{selectedLandingPage.slug}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-6">
                <label className="mb-1 block text-[11px] font-medium uppercase tracking-wider text-ink-4">
                  Internal path
                </label>
                <div className="hair flex items-center overflow-hidden rounded-md border bg-paper focus-within:border-tide">
                  <span className="border-r border-ink/10 bg-canvas/50 px-2.5 py-1.5 font-mono text-[12.5px] text-ink-4">
                    /
                  </span>
                  <input
                    type="text"
                    value={v.targetPath.replace(/^\//, "")}
                    onChange={(e) => {
                      const value = e.target.value;
                      const fullPath = `/${value}`;
                      if (autoSlug) {
                        const nextSlug = slugifyPath(fullPath);
                        if (nextSlug) setField("slug", nextSlug);
                      }
                      setField("targetPath", fullPath);
                    }}
                    placeholder="book"
                    className="flex-1 bg-transparent px-2.5 py-1.5 font-mono text-[12.5px] text-ink-2 focus:outline-none"
                  />
                </div>
                {/* CMS page picker convenience */}
                {cmsPages.length > 0 && (
                  <div className="mt-3">
                    <label className="mb-1 block text-[11px] font-medium uppercase tracking-wider text-ink-4">
                      Or pick a published page
                    </label>
                    <select
                      value=""
                      onChange={(e) => {
                        const page = cmsPages.find((p) => p.id === e.target.value);
                        if (page) {
                          const fullPath = `/${page.slug}`;
                          setField("targetPath", fullPath);
                          if (autoSlug) {
                            const nextSlug = slugifyPath(fullPath);
                            if (nextSlug) setField("slug", nextSlug);
                          }
                        }
                      }}
                      disabled={cmsPagesLoading}
                      className="w-full rounded-md border border-ink/20 bg-paper px-2.5 py-1.5 font-mono text-[12.5px] text-ink-2 focus:outline-none focus:border-tide disabled:opacity-50"
                    >
                      <option value="">Select a page...</option>
                      {cmsPages.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.title || p.slug} (/{p.slug})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Preset chips */}
                <div className="mt-2 flex flex-wrap gap-2">
                  {[
                    { label: "/", path: "/" },
                    { label: "/book (Booking)", path: "/book" },
                    { label: "/pricing", path: "/pricing" },
                  ].map((preset) => (
                    <button
                      key={preset.path}
                      type="button"
                      onClick={() => {
                        setField("targetPath", preset.path);
                        if (autoSlug) {
                          const nextSlug = slugifyPath(preset.path);
                          if (nextSlug) setField("slug", nextSlug);
                        }
                      }}
                      className="hair rounded-md border bg-paper-hi px-3 py-1.5 text-[11px] text-ink-3 hover:border-tide hover:text-tide"
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
                {v.targetPath && (
                  <div className="mt-1 text-[11.5px] text-ink-4">
                    <span className="font-medium">Preview:</span>{" "}
                    <span className="font-mono text-ink-3">
                      https://{tenantDomain}{v.targetPath}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right rail */}
        <aside className="hair-l border-l overflow-y-auto bg-canvas/30 px-4 py-6">
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-[11px] font-medium uppercase tracking-wider text-ink-4">
                Status
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setField("isActive", true)}
                  className={`flex-1 rounded-md px-3 py-2 text-[12.5px] transition ${
                    v.isActive
                      ? "bg-tide text-paper-hi"
                      : "bg-paper-hi text-ink-3 hover:bg-paper-2"
                  }`}
                >
                  Active
                </button>
                <button
                  type="button"
                  onClick={() => setField("isActive", false)}
                  className={`flex-1 rounded-md px-3 py-2 text-[12.5px] transition ${
                    !v.isActive
                      ? "bg-tide text-paper-hi"
                      : "bg-paper-hi text-ink-3 hover:bg-paper-2"
                  }`}
                >
                  Inactive
                </button>
              </div>
            </div>

            {mode === "edit" && (
              <div>
                <label className="mb-1 block text-[11px] font-medium uppercase tracking-wider text-ink-4">
                  Actions
                </label>
                {/* Copy URL button */}
                <button
                  type="button"
                  onClick={copyUrl}
                  disabled={isSaving}
                  className="hair-l w-full rounded-md border border-tide/30 bg-paper-hi px-3 py-2 text-[12.5px] text-tide hover:bg-tide-s/40 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <div className="flex items-center justify-center gap-2">
                    <Copy size={12} strokeWidth={2} />
                    Copy URL
                  </div>
                </button>

                {/* Visit URL link — uses the saved slug so an unsaved
                    slug edit never points at a dead URL. */}
                <a
                  href={`https://${tenantDomain}/${initialValues.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hair-l mt-2 block w-full rounded-md border border-tide/30 bg-paper-hi px-3 py-2 text-[12.5px] text-tide hover:bg-tide-s/40"
                >
                  <div className="flex items-center justify-center gap-2">
                    <ExternalLink size={12} strokeWidth={2} />
                    Visit URL
                  </div>
                </a>

                {onDelete && (
                  <div className="hair mt-3 rounded-lg border border-rust/30 bg-paper-hi p-4">
                    <div className="text-[12.5px] font-medium text-ink-2">
                      Delete this URL
                    </div>
                    <p className="mt-1 text-[11.5px] text-ink-4">
                      Removes the custom URL. Cannot be undone.
                    </p>
                    <button
                      type="button"
                      onClick={onDelete}
                      disabled={isDeleting || isSaving}
                      className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-md border border-rust/30 px-3 py-1.5 text-[12px] font-medium text-rust hover:bg-rust-s/40 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Trash2 size={12} strokeWidth={2} />
                      {isDeleting ? "Deleting…" : "Delete URL"}
                    </button>
                  </div>
                )}

                {updatedAt && (
                  <div className="hair-t border-t pt-3 text-[11.5px] text-ink-4">
                    <span className="text-ink-5">Last updated:</span>{" "}
                    <span className="num text-ink-3">{formatLongDate(updatedAt)}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

export function emptyCustomUrlFormValues(): CustomUrlFormValues {
  return {
    slug: "",
    targetType: "landing_page",
    targetLandingPageId: null,
    targetPath: "/",
    isActive: true,
  };
}

export function customUrlFormValuesFromRecord(record: {
  id: string;
  tenantId: string;
  slug: string;
  targetType: CustomUrlTargetType;
  targetLandingPageId: string | null;
  targetPath: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}): CustomUrlFormValues {
  return {
    slug: record.slug,
    targetType: record.targetType,
    targetLandingPageId: record.targetLandingPageId,
    targetPath: record.targetPath ?? "/",
    isActive: record.isActive,
  };
}

function slugifyPath(path: string): string {
  // Remove leading slash if present
  const pathWithoutSlash = path.replace(/^\//, "");
  // Extract the last segment (slug) from the path
  const lastSegment = pathWithoutSlash.split("/").pop() || "";
  return lastSegment
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");
}

